/**
 * 사용자 관리 다이얼로그 컴포넌트
 * users/management/admin/index.tsx에서 분리
 */
import React from 'react';
import { User } from '@/types';
import {
  EditUserDialog,
  ChangeUserPasswordDialog,
  AuthTypeChangeDialog,
  ConfirmDialog,
  InviteUserDialogWithCustomer,
  EmailResultDialog,
  AlertDialog,
} from '@/components/users/management/common/dialogs';

// InviteUserDialogWithCustomer에서 사용하는 Customer 타입
interface InviteDialogCustomer {
  id: number;
  customer_name: string;
  customer_code: string;
}

interface UserManagementDialogsProps {
  // Edit User
  editingUser: User | null;
  originalUser: User | null;
  loading: boolean;
  onEditUserClose: () => void;
  onSaveUser: (user: User, updates?: { auth_type?: string; new_password?: string }) => Promise<void>;

  // Password Change
  passwordChangeUser: User | null;
  onPasswordChangeClose: () => void;
  onPasswordSubmit: (userId: number, newPassword: string, confirmPassword: string) => Promise<void>;

  // Auth Type Change
  authTypeChangeUser: User | null;
  onAuthTypeChangeClose: () => void;
  onAuthTypeSubmit: (userId: number, authType: 'password' | 'oauth' | 'pending_oauth') => Promise<void>;

  // Deactivate User
  deactivateConfirmOpen: boolean;
  userToDeactivate: User | null;
  onDeactivateConfirm: () => void;
  onDeactivateCancel: () => void;

  // Activate User
  activateConfirmOpen: boolean;
  userToActivate: User | null;
  onActivateConfirm: () => void;
  onActivateCancel: () => void;

  // Delete User
  deleteConfirmOpen: boolean;
  userToDelete: User | null;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;

  // Invite User
  showInviteForm: boolean;
  inviteLoading: boolean;
  customers: InviteDialogCustomer[];
  showCustomerSelect: boolean;
  onInviteClose: () => void;
  onInviteSubmit: (
    emails: string[],
    role: 'admin' | 'user',
    permission: 'readwrite' | 'readonly',
    customerId?: number,
    authType?: 'password' | 'oauth',
    passwordType?: 'manual' | 'random',
    manualPassword?: string,
    sendEmail?: boolean,
    onProgress?: (current: number, total: number) => void
  ) => Promise<void>;

  // Email Result
  emailResultDialogOpen: boolean;
  emailResult: { emailSent: boolean; emailError?: string | null; userEmail: string } | null;
  onEmailResultClose: () => void;

  // Clear Blocked
  clearBlockedConfirmOpen: boolean;
  onClearBlockedConfirm: () => void;
  onClearBlockedCancel: () => void;

  // Alert
  alertOpen: boolean;
  alertTitle: string;
  alertMessage: string;
  onAlertClose: () => void;
}

export const UserManagementDialogs: React.FC<UserManagementDialogsProps> = ({
  editingUser,
  loading,
  onEditUserClose,
  onSaveUser,
  passwordChangeUser,
  onPasswordChangeClose,
  onPasswordSubmit,
  authTypeChangeUser,
  onAuthTypeChangeClose,
  onAuthTypeSubmit,
  deactivateConfirmOpen,
  userToDeactivate,
  onDeactivateConfirm,
  onDeactivateCancel,
  activateConfirmOpen,
  userToActivate,
  onActivateConfirm,
  onActivateCancel,
  deleteConfirmOpen,
  userToDelete,
  onDeleteConfirm,
  onDeleteCancel,
  showInviteForm,
  inviteLoading,
  customers,
  showCustomerSelect,
  onInviteClose,
  onInviteSubmit,
  emailResultDialogOpen,
  emailResult,
  onEmailResultClose,
  clearBlockedConfirmOpen,
  onClearBlockedConfirm,
  onClearBlockedCancel,
  alertOpen,
  alertTitle,
  alertMessage,
  onAlertClose,
}) => {
  return (
    <>
      <EditUserDialog
        user={editingUser}
        loading={loading}
        onClose={onEditUserClose}
        onSave={onSaveUser}
      />

      <ChangeUserPasswordDialog
        user={passwordChangeUser}
        onClose={onPasswordChangeClose}
        onSubmit={onPasswordSubmit}
      />

      <AuthTypeChangeDialog
        user={authTypeChangeUser}
        onClose={onAuthTypeChangeClose}
        onSubmit={onAuthTypeSubmit}
      />

      <ConfirmDialog
        open={deactivateConfirmOpen}
        title="사용자 비활성화"
        description={`정말로 "${userToDeactivate?.email}" 사용자를 비활성화하시겠습니까?`}
        confirmText="비활성화"
        cancelText="취소"
        variant="destructive"
        onConfirm={onDeactivateConfirm}
        onCancel={onDeactivateCancel}
      />

      <ConfirmDialog
        open={activateConfirmOpen}
        title="사용자 활성화"
        description={`정말로 "${userToActivate?.email}" 사용자를 활성화하시겠습니까?`}
        confirmText="활성화"
        cancelText="취소"
        variant="default"
        onConfirm={onActivateConfirm}
        onCancel={onActivateCancel}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="사용자 삭제"
        description={`정말로 "${userToDelete?.email}" 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />

      <InviteUserDialogWithCustomer
        open={showInviteForm}
        onClose={onInviteClose}
        onSubmit={onInviteSubmit}
        loading={inviteLoading}
        customers={customers}
        showCustomerSelect={showCustomerSelect}
      />

      <EmailResultDialog
        open={emailResultDialogOpen}
        onClose={onEmailResultClose}
        emailSent={emailResult?.emailSent ?? false}
        emailError={emailResult?.emailError}
        userEmail={emailResult?.userEmail || ''}
      />

      <ConfirmDialog
        open={clearBlockedConfirmOpen}
        title="차단목록 삭제"
        description="정말로 최근 차단목록을 모두 삭제하시겠습니까?"
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={onClearBlockedConfirm}
        onCancel={onClearBlockedCancel}
      />

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        confirmText="확인"
        variant={alertTitle === '오류' ? 'destructive' : 'default'}
        onConfirm={onAlertClose}
      />
    </>
  );
};

export default UserManagementDialogs;
